// src/components/Home/FeaturedProjects.js
import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaMapMarkerAlt, FaBuilding } from "react-icons/fa";
import { API_URL } from "../../../config/api"; 

const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const getStatusDetails = (status) => {
    switch (status) {
        case "planning": return { label: "SẮP RA MẮT", bgColor: "bg-blue-600", icon: "💡" };
        case "under-construction": return { label: "ĐANG XÂY DỰNG", bgColor: "bg-yellow-600", icon: "🏗️" };
        case "ready": return { label: "ĐÃ HOÀN THÀNH", bgColor: "bg-green-600", icon: "✅" };
        case "sold-out": return { label: "ĐÃ BÁN HẾT", bgColor: "bg-red-600", icon: "🚫" };
        case "inactive": return { label: "TẠM DỪNG", bgColor: "bg-gray-500", icon: "⏸️" };
        default: return { label: status, bgColor: "bg-gray-700", icon: "" };
    }
};

// Hàm xử lý ảnh an toàn
const getImages = (imageData) => {
    if (!imageData) return [];

    if (Array.isArray(imageData)) return imageData;

    if (typeof imageData === "object") return [];

    if (typeof imageData === "string") {
        try {
            const parsed = JSON.parse(imageData);
            return Array.isArray(parsed) ? parsed : [parsed];
        } catch {
            return [imageData]; // string bình thường
        }
    }

    return [];
};

const ProjectCard = ({ project, index }) => {


    const images = getImages(project.image);
    const mainImage =
        images.length > 0
            ? images[0].startsWith("http")
                ? images[0]
                : `${API_URL}${images[0]}`
            : "/default-image.png";

    const status = getStatusDetails(project.status);

    return (
        <motion.div
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.15 }}
            className="relative border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition flex flex-col group bg-white"
        >
            <Link to={project.url || "#"} className="flex flex-col flex-1">
                <div className="relative">
                    <img
                        loading="lazy"
                        src={mainImage}
                        alt={project.name}
                        className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {project.status && (
                        <span
                            className={`absolute top-2 left-2 text-xs text-white px-2 py-1 rounded ${status.bgColor} flex items-center gap-1 shadow-lg`}
                        >
                            <span>{status.icon}</span> <span>{status.label}</span>
                        </span>
                    )}
                </div>

                <div className="p-3 flex flex-col flex-1 text-sm text-gray-700 space-y-1">
                    <h3 className="font-semibold text-base line-clamp-2 text-[#0c2c4d]">{project.name}</h3>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                        <FaBuilding /> {project.developer}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                        <FaMapMarkerAlt /> {project.location || "Đang cập nhật"}
                    </div>
                    {project.yearCompleted && (
                        <div className="text-xs text-gray-400">Năm hoàn thành: {project.yearCompleted}</div>
                    )}
                </div>

                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="bg-[#c9a063] text-white px-4 py-2 rounded-lg font-semibold shadow-lg hover:bg-[#a68652]">Xem chi tiết</span>
                </div>
            </Link>
        </motion.div>
    );
};

export default function FeaturedProjects({ projects }) {
    return (
        <section className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.h2
                    className="text-3xl sm:text-4xl font-extrabold text-center text-[#0c2c4d] mb-12"
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    Dự án <span className="text-amber-500">Nổi bật</span>
                </motion.h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {projects.slice(0, 4).map((project, idx) => (
                        <ProjectCard key={project.id} project={project} index={idx} />
                    ))}
                </div>

                <div className="mt-12 text-center">
                    <Link
                        to="/projects"
                        className="inline-flex items-center px-8 py-3 bg-[#c9a063] text-white font-semibold text-lg rounded-lg shadow hover:bg-[#a68652] transition transform hover:scale-105"
                    >
                        Xem tất cả dự án
                    </Link>
                </div>
            </div>
        </section>
    );
}
